'use client';

import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
  MapPin,
  ChevronRight,
  Search,
  Building2,
  Navigation,
  X,
} from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { Input } from '@kit/ui/input';
import { cn } from '@kit/ui/utils';

import { ImageCarousel } from '../../_components/image-carousel';
import type { PublicLocationRow } from '../_lib/server/booking-page.loader';

interface LocationFunnelPickerProps {
  /** Rich public-location rows (from get_public_locations). */
  locations: PublicLocationRow[];
  selectedLocationId?: string | null;
  onLocationSelect: (locationId: string) => void;
  /** Gallery images per location id (court_locations.image_urls). */
  imagesByLocation?: Record<string, string[]>;
  /** When true, prefer the Arabic (_alt) value for every label. */
  preferAlt?: boolean;
  /** Fold to a "selected · Change" pill once a club is chosen (inline on
   *  /book). The venue picker popup turns it off: it is opened to change. */
  collapseOnSelect?: boolean;
}

type Gender = 'male' | 'female' | 'both';

/** Resolve the display value for a bilingual pair, honoring the active locale. */
function pickLabel(
  primary: string | null,
  alt: string | null,
  preferAlt: boolean,
): string {
  if (preferAlt) return (alt && alt.trim()) || (primary ?? '');
  return (primary ?? '') || (alt ?? '');
}

const UNGROUPED = '__ungrouped__';

/**
 * Progressive, self-collapsing location picker for tenants with many venues
 * (schools). Funnels Region → City → Gender → Club (name + neighborhood), then
 * hands the chosen location to the standard sport step. Levels with a single
 * option auto-skip, so the user only ever sees a real decision. A search box
 * jumps straight to a club by name/neighborhood, and breadcrumb chips backtrack.
 */
export function LocationFunnelPicker({
  locations,
  selectedLocationId,
  onLocationSelect,
  imagesByLocation,
  preferAlt = false,
  collapseOnSelect = true,
}: LocationFunnelPickerProps) {
  const { t } = useTranslation('bookings');

  // Funnel selections (null = not yet chosen at that level).
  const [regionId, setRegionId] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [gender, setGender] = useState<Gender | null>(null);
  const [search, setSearch] = useState('');
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);

  const selected = useMemo(
    () => locations.find((l) => l.id === selectedLocationId) ?? null,
    [locations, selectedLocationId],
  );

  // ── Collapsed pill (after a club is chosen) ───────────────────────────────
  // Whenever a location is selected — by tapping a club, deep link, or
  // localStorage rehydration — collapse to a compact "selected" pill with a
  // Change affordance. This is the picker's confirmation state on BOTH mobile
  // and desktop; without it a deep-linked desktop user sees the full funnel as
  // if nothing were chosen. (collapseOnSelect previously gated this to native,
  // which left desktop with no selected-state indication.)
  const isCollapsed = collapseOnSelect && !!selected && !isManuallyExpanded;

  if (isCollapsed && selected) {
    return (
      <button
        type="button"
        onClick={() => setIsManuallyExpanded(true)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-primary/40 bg-card px-3 py-2.5 text-start shadow-sm transition-colors hover:border-primary hover:bg-primary/5"
      >
        <div className="flex min-w-0 items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate text-sm font-medium">
            {pickLabel(selected.name, selected.name_alt, preferAlt)}
          </span>
          {selected.neighborhood && (
            <span className="truncate text-xs text-muted-foreground">
              · {pickLabel(selected.neighborhood, selected.neighborhood_alt, preferAlt)}
            </span>
          )}
        </div>
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
          {t('change', { defaultValue: 'Change' })}
          <ChevronRight className="h-3.5 w-3.5 rtl:rotate-180" />
        </span>
      </button>
    );
  }

  // ── Search mode: match clubs by name/neighborhood across the whole tenant ──
  const trimmedSearch = search.trim().toLowerCase();
  const searchResults = trimmedSearch
    ? locations.filter((l) => {
        const hay = [
          l.name,
          l.name_alt,
          l.neighborhood,
          l.neighborhood_alt,
          l.city,
          l.city_alt,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(trimmedSearch);
      })
    : null;

  // ── Funnel filtering: narrow the pool by current selections ───────────────
  const afterRegion = regionId
    ? locations.filter((l) => (l.region_id ?? UNGROUPED) === regionId)
    : locations;
  const afterCity = city
    ? afterRegion.filter((l) => (l.city ?? UNGROUPED) === city)
    : afterRegion;
  const afterGender =
    gender && gender !== 'both'
      ? afterCity.filter((l) => l.gender === gender || l.gender === 'both')
      : afterCity;

  // Distinct options at each level (computed against the upstream pool).
  const regionOptions = distinctBy(
    locations,
    (l) => l.region_id ?? UNGROUPED,
    (l) => pickLabel(l.region_name, l.region_name_alt, preferAlt) || t('ungroupedRegion', 'Other'),
  );
  const cityOptions = distinctBy(
    afterRegion,
    (l) => l.city ?? UNGROUPED,
    (l) => pickLabel(l.city, l.city_alt, preferAlt) || t('ungroupedCity', 'Other'),
  );
  const genderOptions: { key: Gender; label: string }[] = (() => {
    const present = new Set(afterCity.map((l) => l.gender));
    const opts: { key: Gender; label: string }[] = [];
    if (present.has('male')) opts.push({ key: 'male', label: t('genderMale', 'Male') });
    if (present.has('female')) opts.push({ key: 'female', label: t('genderFemale', 'Female') });
    // "All / Both" only meaningful when both single-gender facilities exist.
    if (present.has('male') && present.has('female')) {
      opts.unshift({ key: 'both', label: t('genderAll', 'All') });
    }
    return opts;
  })();

  // Auto-skip: a level with ≤1 real option contributes no decision.
  const needRegion = regionOptions.length > 1 && !regionId;
  const needCity = !needRegion && cityOptions.length > 1 && !city;
  const needGender =
    !needRegion && !needCity && genderOptions.length > 1 && !gender;
  const showClubs = !needRegion && !needCity && !needGender;

  const clubs = afterGender;

  const reset = () => {
    setRegionId(null);
    setCity(null);
    setGender(null);
    setSearch('');
  };

  // Breadcrumb chips for the committed selections.
  const crumbs: { label: string; onClick: () => void }[] = [];
  if (regionId) {
    crumbs.push({
      label: regionOptions.find((o) => o.key === regionId)?.label ?? '',
      onClick: () => {
        setRegionId(null);
        setCity(null);
        setGender(null);
      },
    });
  }
  if (city) {
    crumbs.push({
      label: cityOptions.find((o) => o.key === city)?.label ?? '',
      onClick: () => {
        setCity(null);
        setGender(null);
      },
    });
  }
  if (gender) {
    crumbs.push({
      label: genderOptions.find((o) => o.key === gender)?.label ?? gender,
      onClick: () => setGender(null),
    });
  }

  const handleClubSelect = (id: string) => {
    onLocationSelect(id);
    setIsManuallyExpanded(false);
  };

  return (
    <div className="w-full min-w-0 max-w-full space-y-3">
      {/* Search — always available, jumps straight to a club */}
      <div className="relative">
        <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('searchClubs', 'Search by club or neighborhood…')}
          className="ps-9"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label={t('clear', 'Clear')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Breadcrumb chips (funnel mode only) */}
      {!searchResults && crumbs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {crumbs.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={c.onClick}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted/70"
            >
              {c.label}
              <X className="h-3 w-3 opacity-60" />
            </button>
          ))}
          <button
            type="button"
            onClick={reset}
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            {t('reset', 'Reset')}
          </button>
        </div>
      )}

      {/* ── Search results ─────────────────────────────────────────────── */}
      {searchResults && (
        <ClubList
          clubs={searchResults}
          selectedLocationId={selectedLocationId}
          onSelect={handleClubSelect}
          imagesByLocation={imagesByLocation}
          preferAlt={preferAlt}
          emptyLabel={t('noClubsMatch', 'No clubs match your search.')}
          capHeight
        />
      )}

      {/* ── Funnel: Region ─────────────────────────────────────────────── */}
      {!searchResults && needRegion && (
        <OptionGrid
          title={t('selectRegion', 'Select a region')}
          icon={Navigation}
          options={regionOptions}
          onSelect={(key) => setRegionId(key)}
        />
      )}

      {/* ── Funnel: City ───────────────────────────────────────────────── */}
      {!searchResults && needCity && (
        <OptionGrid
          title={t('selectCity', 'Select a city')}
          icon={MapPin}
          options={cityOptions}
          onSelect={(key) => setCity(key)}
        />
      )}

      {/* ── Funnel: Gender ─────────────────────────────────────────────── */}
      {!searchResults && needGender && (
        <OptionGrid
          title={t('selectGender', 'Select facility type')}
          icon={Building2}
          options={genderOptions.map((g) => ({ key: g.key, label: g.label }))}
          onSelect={(key) => setGender(key as Gender)}
        />
      )}

      {/* ── Funnel: Clubs ──────────────────────────────────────────────── */}
      {!searchResults && showClubs && (
        <ClubList
          clubs={clubs}
          selectedLocationId={selectedLocationId}
          onSelect={handleClubSelect}
          imagesByLocation={imagesByLocation}
          preferAlt={preferAlt}
          emptyLabel={t('noClubsHere', 'No clubs available here.')}
        />
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

interface Option {
  key: string;
  label: string;
}

/** Distinct (key → label) options, first label wins, sorted by label. */
function distinctBy(
  rows: PublicLocationRow[],
  keyOf: (l: PublicLocationRow) => string,
  labelOf: (l: PublicLocationRow) => string,
): Option[] {
  const map = new Map<string, string>();
  for (const r of rows) {
    const k = keyOf(r);
    if (!map.has(k)) map.set(k, labelOf(r));
  }
  return Array.from(map.entries())
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

interface OptionGridProps {
  title: string;
  icon: typeof MapPin;
  options: Option[];
  onSelect: (key: string) => void;
}

function OptionGrid({ title, icon: Icon, options, onSelect }: OptionGridProps) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {options.map((o) => (
          <Button
            key={o.key}
            type="button"
            variant="outline"
            onClick={() => onSelect(o.key)}
            className="h-auto w-full min-w-0 justify-between gap-2 px-3 py-3 text-start font-normal"
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{o.label}</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:rotate-180" />
          </Button>
        ))}
      </div>
    </div>
  );
}

interface ClubListProps {
  clubs: PublicLocationRow[];
  selectedLocationId?: string | null;
  onSelect: (id: string) => void;
  imagesByLocation?: Record<string, string[]>;
  preferAlt: boolean;
  emptyLabel: string;
  /** When true, cap the list to ~5 cards and scroll the rest (search results,
   *  which can be long — keeps the drawer from growing unbounded). */
  capHeight?: boolean;
}

function ClubList({
  clubs,
  selectedLocationId,
  onSelect,
  imagesByLocation,
  preferAlt,
  emptyLabel,
  capHeight = false,
}: ClubListProps) {
  const { t } = useTranslation('bookings');

  if (clubs.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        {emptyLabel}
      </p>
    );
  }

  const sorted = [...clubs].sort((a, b) =>
    pickLabel(a.name, a.name_alt, preferAlt).localeCompare(
      pickLabel(b.name, b.name_alt, preferAlt),
    ),
  );

  return (
    // grid-cols-1 on mobile, 2-up from sm so image cards fill the column when it
    // has room (mirrors the delta landing grid, scaled to the booking column).
    // min-w-0 on the track AND each Card lets long club names truncate instead
    // of widening a card past the container's right edge.
    // capHeight: scroll after a few rows (search results can be long). The small
    // padding gives the cards' hover shadow room inside the scroll viewport so
    // it isn't clipped against the edges. (The selection ring is ring-inset, so
    // it never needs outward room.)
    <div
      className={cn(
        'grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2',
        capHeight && 'max-h-[34rem] overflow-y-auto p-1',
      )}
    >
      {sorted.map((club) => {
        const isSelected = selectedLocationId === club.id;
        const name = pickLabel(club.name, club.name_alt, preferAlt);
        const neighborhood = pickLabel(
          club.neighborhood,
          club.neighborhood_alt,
          preferAlt,
        );
        const images = imagesByLocation?.[club.id];
        return (
          <ClubCard
            key={club.id}
            slug={club.slug}
            name={name}
            neighborhood={neighborhood}
            images={images}
            isSelected={isSelected}
            onSelect={() => onSelect(club.id)}
            genderSuffix={
              club.gender === 'male'
                ? t('genderMale', 'Male')
                : club.gender === 'female'
                  ? t('genderFemale', 'Female')
                  : null
            }
          />
        );
      })}
    </div>
  );
}

interface ClubCardProps {
  slug: string;
  name: string;
  neighborhood: string;
  /** Ordered gallery URLs (court_locations.image_urls); every one is shown. */
  images?: string[];
  isSelected: boolean;
  onSelect: () => void;
  /** "Male"/"Female" appended to single-gender venue titles; null for "both". */
  genderSuffix: string | null;
}

/**
 * A single selectable club card. Two presentations, chosen per-card by whether
 * the location has a photo:
 *  • image-on-top (delta landing style) when photos exist — a 4/3 gallery
 *    over the name + neighborhood;
 *  • the compact row (Building2 icon + text + chevron) when it doesn't, so
 *    photo-less venues stay tidy in a mixed grid.
 * ring-inset draws the selection highlight INSIDE the card box so the bounding
 * grid / scroll container can't clip its left/right edges.
 */
function ClubCard({
  slug,
  name,
  neighborhood,
  images,
  isSelected,
  onSelect,
  genderSuffix,
}: ClubCardProps) {
  const title = genderSuffix ? `${name} - ${genderSuffix}` : name;

  if (images?.length) {
    return (
      <Card
        data-test="location-card"
        data-test-location-slug={slug}
        className={cn(
          'min-w-0 cursor-pointer overflow-hidden p-0 transition-all hover:shadow-md',
          isSelected && 'ring-2 ring-inset ring-primary',
        )}
        onClick={onSelect}
      >
        {/* Every photo, not just the first. The carousel's controls
            stopPropagation, so paging the gallery doesn't select the club —
            but a tap on the photo itself still does. Its own root already is
            `relative aspect-[4/3] w-full overflow-hidden bg-muted`. */}
        <ImageCarousel images={images} alt={name} priority={false} />
        <CardContent className="p-3">
          {/* Wrap up to 2 lines (school names are long + meaningful) then
              ellipsize. line-clamp keeps the card height bounded. */}
          <h3 className="line-clamp-2 font-semibold leading-snug">{title}</h3>
          {neighborhood && (
            <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate">{neighborhood}</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      data-test="location-card"
      data-test-location-slug={slug}
      className={cn(
        'min-w-0 cursor-pointer transition-all hover:shadow-md',
        isSelected && 'ring-2 ring-inset ring-primary',
      )}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted">
              <Building2 className="h-5 w-5 text-muted-foreground" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-2 font-semibold leading-snug">
                {title}
              </h3>
              {neighborhood && (
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {neighborhood}
                </p>
              )}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:rotate-180" />
        </div>
      </CardContent>
    </Card>
  );
}
